import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import {
  getDownloadURL,
  getMetadata,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import {
  DEFAULT_PRESET_ID,
  DEFAULT_VOICE_ID,
  ELEVENLABS_PRESETS,
  ELEVENLABS_VOICES,
  ElevenLabsVoiceSettings,
  WotdVoiceConfig,
  getElevenLabsPreset,
  normalizeElevenLabsSettings,
  normalizeWotdVoiceConfig,
  shouldUseVoiceDefaults,
} from '../../lib/aiVoice';

/**
 * Word-of-the-Day voice config. Mirrors the Pulse /admin/ai-voice page
 * (provider/voice/preset/sliders/preview/save loop) but scoped to the
 * single Parrot use case: which ElevenLabs voice should pronounce the
 * daily WOTD when a user taps the speaker icon.
 *
 * Persistence: Firestore doc `app-config/wotd-voice`. The iOS app
 * reads it once at app launch (ElevenLabsTTSService) and uses the
 * voiceId + preset + settings for every WOTD pronunciation that day.
 *
 * Preview path: POST /api/tts-wotd → Netlify function → ElevenLabs.
 * Audio plays inline in the browser; nothing is persisted to Firestore
 * from the preview (only the explicit "Save" button writes config).
 *
 * App audio path: the pre-generation panel renders each WOTD with the
 * current controls, then uploads mp3s to Firebase Storage at
 * `wotd-pronunciations/<word-slug>.mp3` for iOS to preload.
 */

const CONFIG_COLLECTION = 'app-config';
const CONFIG_DOC_ID = 'wotd-voice';

// One-word vocab is the dominant payload (Word of the Day) but it's
// useful to preview a short sentence too — picks up emphasis / pauses
// that single words don't exercise.
const SAMPLE_SCRIPTS = [
  'Rambunctious',
  'Ubiquity',
  'The ubiquity of mobile phones means you don\'t really need a watch.',
  'Apposite. Apt in the circumstances or in relation to something.',
];

type WotdAudioRow = {
  word: string;
  storagePath: string;
  remoteURL: string | null;
  remoteUpdatedAt: number | null;
  generating: boolean;
  error: string | null;
  pronunciationOverride: WotdPronunciationOverride | null;
};

type WotdPronunciationOverride = {
  alphabet: 'cmu-arpabet';
  phoneme: string;
};

const wordSlug = (word: string) =>
  word
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const wotdAudioStoragePath = (word: string) => `wotd-pronunciations/${wordSlug(word)}.mp3`;

const CMU_PHONEME_PATTERN = /^[A-Z0-2 ]{2,160}$/;

const normalizeCmuPronunciation = (value: unknown) =>
  typeof value === 'string'
    ? value.trim().toUpperCase().replace(/\s+/g, ' ')
    : '';

const wotdPronunciationOverrideFromData = (
  data: Record<string, unknown>
): WotdPronunciationOverride | null => {
  const override = data.pronunciationOverride;
  if (override && typeof override === 'object') {
    const payload = override as Record<string, unknown>;
    const alphabet =
      typeof payload.alphabet === 'string'
        ? payload.alphabet.trim().toLowerCase()
        : 'cmu-arpabet';
    const phoneme = normalizeCmuPronunciation(payload.phoneme);
    if (alphabet === 'cmu-arpabet' && CMU_PHONEME_PATTERN.test(phoneme)) {
      return { alphabet: 'cmu-arpabet', phoneme };
    }
  }

  const legacyPhoneme = normalizeCmuPronunciation(data.cmuPronunciation);
  if (CMU_PHONEME_PATTERN.test(legacyPhoneme)) {
    return { alphabet: 'cmu-arpabet', phoneme: legacyPhoneme };
  }

  return null;
};

const remoteAgeLabel = (updatedAt: number | null) => {
  if (!updatedAt) return null;
  const minutes = Math.round((Date.now() - updatedAt) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

export const VoiceAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [audioRows, setAudioRows] = useState<WotdAudioRow[]>([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioGenerating, setAudioGenerating] = useState(false);
  const [audioProgress, setAudioProgress] = useState<string | null>(null);

  const [voiceId, setVoiceId] = useState<string>(DEFAULT_VOICE_ID);
  const [presetId, setPresetId] = useState<string>(DEFAULT_PRESET_ID);
  const [settings, setSettings] = useState<ElevenLabsVoiceSettings>(
    normalizeElevenLabsSettings(undefined, DEFAULT_PRESET_ID)
  );
  const [punctuationPauses, setPunctuationPauses] = useState(false);
  const [sampleText, setSampleText] = useState(SAMPLE_SCRIPTS[0]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  const hydrateWotdAudioStatus = async (): Promise<WotdAudioRow[]> => {
    const database = db;
    const s = storage;
    if (!database || !s) return [];
    setAudioLoading(true);
    try {
      const snap = await getDocs(collection(database, 'wordbank', 'wod', 'words'));
      const wordDocs = [...snap.docs].sort((a, b) => a.id.localeCompare(b.id));
      const rows = await Promise.all(
        wordDocs.map(async (wordDoc) => {
          const word = wordDoc.id;
          const storagePath = wotdAudioStoragePath(word);
          const pronunciationOverride = wotdPronunciationOverrideFromData(
            wordDoc.data() as Record<string, unknown>
          );
          try {
            const ref = storageRef(s, storagePath);
            const [remoteURL, meta] = await Promise.all([
              getDownloadURL(ref),
              getMetadata(ref),
            ]);
            return {
              word,
              storagePath,
              remoteURL,
              remoteUpdatedAt: new Date(meta.updated).getTime(),
              generating: false,
              error: null,
              pronunciationOverride,
            };
          } catch {
            return {
              word,
              storagePath,
              remoteURL: null,
              remoteUpdatedAt: null,
              generating: false,
              error: null,
              pronunciationOverride,
            };
          }
        })
      );
      setAudioRows(rows);
      return rows;
    } catch (e: any) {
      setError(e?.message || 'Failed to load WOTD audio status');
      return [];
    } finally {
      setAudioLoading(false);
    }
  };

  // ── Load existing config

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db!, CONFIG_COLLECTION, CONFIG_DOC_ID));
        if (snap.exists()) {
          const data = normalizeWotdVoiceConfig(snap.data() as Partial<WotdVoiceConfig>);
          setVoiceId(data.voiceId);
          setPresetId(data.presetId);
          setSettings(
            shouldUseVoiceDefaults(data.presetId)
              ? normalizeElevenLabsSettings(undefined, DEFAULT_PRESET_ID)
              : normalizeElevenLabsSettings(data.elevenLabsSettings || undefined, data.presetId)
          );
          setPunctuationPauses(Boolean(data.punctuationPauses));
          setSavedAt(data.updatedAt);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load voice config');
      } finally {
        setLoading(false);
      }
    };
    load();
    hydrateWotdAudioStatus();
    return () => {
      stopPlayback();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived

  const usingVoiceDefaults = useMemo(() => shouldUseVoiceDefaults(presetId), [presetId]);
  const selectedVoice = useMemo(
    () => ELEVENLABS_VOICES.find((v) => v.id === voiceId) || ELEVENLABS_VOICES[0],
    [voiceId]
  );
  const authoredAudioCount = useMemo(
    () => audioRows.filter((row) => row.remoteURL).length,
    [audioRows]
  );

  // ── Preset / slider handling

  const applyPreset = (nextPresetId: string) => {
    const preset = getElevenLabsPreset(nextPresetId);
    setPresetId(preset.id);
    if (preset.settings) {
      setSettings(normalizeElevenLabsSettings(preset.settings, preset.id));
    }
  };

  const updateSetting = <K extends keyof ElevenLabsVoiceSettings>(
    key: K,
    value: ElevenLabsVoiceSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    // Manual changes leave the preset name as-is but switch the saved
    // payload to the explicit slider values. Default preset stays
    // sticky so toggling back to "Default" still wipes overrides.
  };

  // ── Preview

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setPlaying(false);
  };

  const handlePreview = async () => {
    setError(null);
    stopPlayback();
    setPreviewing(true);
    try {
      const resp = await fetch('/api/tts-wotd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: sampleText,
          voice: voiceId,
          presetId,
          settings: usingVoiceDefaults ? null : settings,
          punctuationPauses,
        }),
      });
      if (!resp.ok) {
        let message = `Preview failed (HTTP ${resp.status})`;
        try {
          const payload = await resp.json();
          message = payload?.error || message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setPlaying(true);
      audio.onended = () => stopPlayback();
      audio.onerror = () => stopPlayback();
      await audio.play();
    } catch (e: any) {
      setError(e?.message || 'Preview failed');
      stopPlayback();
    } finally {
      setPreviewing(false);
    }
  };

  // ── Pre-generated WOTD pronunciation files

  const patchAudioRow = (word: string, patch: Partial<WotdAudioRow>) => {
    setAudioRows((prev) =>
      prev.map((row) => (row.word === word ? { ...row, ...patch } : row))
    );
  };

  const handleGenerateWotdAudio = async (mode: 'missing' | 'all') => {
    const s = storage;
    if (!s) {
      setError('Firebase Storage is not configured.');
      return;
    }

    setError(null);
    setAudioProgress(null);
    setAudioGenerating(true);

    try {
      const rows = audioRows.length ? audioRows : await hydrateWotdAudioStatus();
      const targets = mode === 'missing'
        ? rows.filter((row) => !row.remoteURL)
        : rows;

      if (!targets.length) {
        setAudioProgress('All WOTD pronunciation files are already authored.');
        return;
      }

      for (let i = 0; i < targets.length; i += 1) {
        const row = targets[i];
        patchAudioRow(row.word, { generating: true, error: null });
        setAudioProgress(`${i + 1}/${targets.length}: generating ${row.word}`);

        try {
          const resp = await fetch('/api/tts-wotd', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: row.word,
              voice: voiceId,
              presetId,
              settings: usingVoiceDefaults ? null : settings,
              punctuationPauses: false,
              pronunciationOverride: row.pronunciationOverride,
            }),
          });

          if (!resp.ok) {
            let message = `Generate failed (HTTP ${resp.status})`;
            try {
              const payload = await resp.json();
              message = payload?.error || message;
            } catch {
              /* ignore */
            }
            throw new Error(message);
          }

          const blob = await resp.blob();
          const ref = storageRef(s, row.storagePath);
          await uploadBytes(ref, blob, {
            contentType: 'audio/mpeg',
            cacheControl: 'no-cache',
            customMetadata: {
              word: row.word,
              voiceId,
              presetId,
              modelId: 'eleven_flash_v2',
              languageCode: 'en',
              generatedAt: String(Date.now()),
              ...(row.pronunciationOverride
                ? {
                    pronunciationAlphabet: row.pronunciationOverride.alphabet,
                    pronunciationOverride: row.pronunciationOverride.phoneme,
                  }
                : {}),
            },
          });

          const [remoteURL, meta] = await Promise.all([
            getDownloadURL(ref),
            getMetadata(ref),
          ]);
          patchAudioRow(row.word, {
            remoteURL,
            remoteUpdatedAt: new Date(meta.updated).getTime(),
            generating: false,
            error: null,
          });
        } catch (e: any) {
          patchAudioRow(row.word, {
            generating: false,
            error: e?.message || 'Generate failed',
          });
        }
      }

      setAudioProgress('WOTD pronunciation generation finished.');
    } finally {
      setAudioGenerating(false);
    }
  };

  // ── Save

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: WotdVoiceConfig = {
        provider: 'elevenlabs',
        voiceId,
        presetId,
        elevenLabsSettings: usingVoiceDefaults ? null : settings,
        punctuationPauses,
        updatedAt: Date.now(),
      };
      await setDoc(doc(db!, CONFIG_COLLECTION, CONFIG_DOC_ID), payload, { merge: true });
      setSavedAt(payload.updatedAt);
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── UI

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-sm text-neutral-500">Loading voice config…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">Voice</div>
            <div className="text-xs text-neutral-500">
              Which voice pronounces the Word of the Day. Saved config applies app-wide on the next iOS launch.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button
              onClick={signOutUser}
              className="text-xs text-neutral-600 underline hover:text-neutral-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <AdminTabs current="voice" />

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {/* Voice picker */}
        <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-neutral-900">Voice</div>
              <div className="text-xs text-neutral-500 mt-0.5">
                ElevenLabs catalog voices. Sarah is the recommended WOTD default.
              </div>
            </div>
            <div className="text-xs text-neutral-400 font-mono">
              id: {voiceId}
            </div>
          </div>
          <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ELEVENLABS_VOICES.map((voice) => {
              const isActive = voice.id === voiceId;
              return (
                <button
                  key={voice.id}
                  onClick={() => setVoiceId(voice.id)}
                  className={`text-left rounded-lg border px-4 py-3 transition ${
                    isActive
                      ? 'border-neutral-900 bg-neutral-900 text-white'
                      : 'border-neutral-200 bg-white text-neutral-900 hover:border-neutral-400'
                  }`}
                >
                  <div className="text-sm font-semibold">{voice.label}</div>
                  {voice.description && (
                    <div
                      className={`text-xs mt-0.5 ${
                        isActive ? 'text-neutral-300' : 'text-neutral-500'
                      }`}
                    >
                      {voice.description}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Preset chips */}
        <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100">
            <div className="text-sm font-semibold text-neutral-900">Style preset</div>
            <div className="text-xs text-neutral-500 mt-0.5">
              Quick personality controls. "Default" hands off to the voice's own ElevenLabs settings.
            </div>
          </div>
          <div className="px-6 py-5 space-y-3">
            <div className="flex flex-wrap gap-2">
              {ELEVENLABS_PRESETS.map((preset) => {
                const isActive = preset.id === presetId;
                return (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                      isActive
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-neutral-500">{getElevenLabsPreset(presetId).description}</div>
          </div>
        </section>

        {/* Sliders */}
        {!usingVoiceDefaults && (
          <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100">
              <div className="text-sm font-semibold text-neutral-900">Fine controls</div>
              <div className="text-xs text-neutral-500 mt-0.5">
                Optional overrides. Switching preset resets these to the preset's defaults.
              </div>
            </div>
            <div className="px-6 py-5 space-y-5">
              <Slider
                label="Stability"
                description="Lower = more emotional variation. Higher = more consistent."
                value={settings.stability}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => updateSetting('stability', v)}
              />
              <Slider
                label="Similarity boost"
                description="How tightly to match the voice's original timbre."
                value={settings.similarityBoost}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => updateSetting('similarityBoost', v)}
              />
              <Slider
                label="Style"
                description="Stronger inflection / accent imitation. High values can sound theatrical."
                value={settings.style}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => updateSetting('style', v)}
              />
              <Slider
                label="Speed"
                description="Playback speed. 1.0 is natural; under 1.0 reads slower."
                value={settings.speed}
                min={0.7}
                max={1.2}
                step={0.01}
                onChange={(v) => updateSetting('speed', v)}
              />

              <label className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  checked={settings.useSpeakerBoost}
                  onChange={(e) => updateSetting('useSpeakerBoost', e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-neutral-700">
                  <span className="font-medium">Speaker boost</span>
                  <span className="text-xs text-neutral-500 block">
                    ElevenLabs flag that amplifies similarity to the original speaker.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={punctuationPauses}
                  onChange={(e) => setPunctuationPauses(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-neutral-700">
                  <span className="font-medium">Punctuation pauses</span>
                  <span className="text-xs text-neutral-500 block">
                    Insert line breaks after sentence-end punctuation. Helps multi-sentence reads;
                    typically leave OFF for single-word WOTD.
                  </span>
                </span>
              </label>
            </div>
          </section>
        )}

        {/* Preview */}
        <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100">
            <div className="text-sm font-semibold text-neutral-900">Preview</div>
            <div className="text-xs text-neutral-500 mt-0.5">
              Hear how {selectedVoice.label} reads a sample. Not saved until you press Save.
            </div>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div className="flex flex-wrap gap-2">
              {SAMPLE_SCRIPTS.map((s) => (
                <button
                  key={s}
                  onClick={() => setSampleText(s)}
                  className={`px-3 py-1.5 rounded-md text-xs border transition ${
                    s === sampleText
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                  }`}
                >
                  {s.length > 36 ? `${s.slice(0, 33)}…` : s}
                </button>
              ))}
            </div>
            <textarea
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={handlePreview}
                disabled={previewing || !sampleText.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 transition disabled:opacity-60"
              >
                {previewing ? 'Generating…' : playing ? 'Playing…' : '▶︎ Preview'}
              </button>
              {playing && (
                <button
                  onClick={stopPlayback}
                  className="px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:border-neutral-400"
                >
                  Stop
                </button>
              )}
              <div className="text-xs text-neutral-400">
                {sampleText.length}/800 chars
              </div>
            </div>
          </div>
        </section>

        {/* Storage-backed app audio */}
        <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-neutral-900">Pre-generated app audio</div>
              <div className="text-xs text-neutral-500 mt-0.5">
                Generate English mp3 pronunciations for every WOTD and save them to Firebase Storage.
                iOS preloads today's file from <code>wotd-pronunciations/&lt;word&gt;.mp3</code>.
              </div>
            </div>
            <div className="text-xs text-neutral-500 font-mono shrink-0">
              {authoredAudioCount}/{audioRows.length || '—'} authored
            </div>
          </div>
          <div className="px-6 py-5 space-y-4">
            {!storage && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm rounded-lg px-4 py-3">
                Firebase Storage is not configured. Add the web app storage env vars before generating files.
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handleGenerateWotdAudio('missing')}
                disabled={audioGenerating || audioLoading || !storage}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 transition disabled:opacity-60"
              >
                {audioGenerating ? 'Generating…' : 'Generate missing'}
              </button>
              <button
                onClick={() => handleGenerateWotdAudio('all')}
                disabled={audioGenerating || audioLoading || !storage || !audioRows.length}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:border-neutral-400 transition disabled:opacity-60"
              >
                Regenerate all
              </button>
              <button
                onClick={() => { void hydrateWotdAudioStatus(); }}
                disabled={audioGenerating || audioLoading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:border-neutral-400 transition disabled:opacity-60"
              >
                {audioLoading ? 'Refreshing…' : 'Refresh status'}
              </button>
              {audioProgress && (
                <span className="text-xs text-neutral-500">{audioProgress}</span>
              )}
            </div>

            <div className="text-xs text-neutral-500">
              Generation uses the currently selected voice and style controls above. Save config separately
              if you also want the fallback live-TTS path to match these files.
            </div>

            <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
              {audioRows.map((row) => {
                const age = remoteAgeLabel(row.remoteUpdatedAt);
                return (
                  <div
                    key={row.word}
                    className="px-4 py-3 flex items-center justify-between gap-4 bg-white"
                  >
                    <div>
                      <div className="text-sm font-semibold text-neutral-900">{row.word}</div>
                      <div className="text-[10px] font-mono text-neutral-400">
                        {row.storagePath}
                      </div>
                      {row.pronunciationOverride && (
                        <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                          CMU {row.pronunciationOverride.phoneme}
                        </div>
                      )}
                      {row.error && <div className="text-xs text-red-600 mt-1">{row.error}</div>}
                    </div>
                    <div className="text-xs text-right shrink-0">
                      {row.generating ? (
                        <span className="text-neutral-500">Generating…</span>
                      ) : row.remoteURL ? (
                        <a
                          href={row.remoteURL}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-green-700 hover:underline"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          Authored{age ? ` · ${age}` : ''}
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-neutral-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-neutral-300" />
                          Missing
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {!audioLoading && !audioRows.length && (
                <div className="px-4 py-6 text-sm text-neutral-500">
                  No WOTD words found in Firestore yet.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Save */}
        <section className="flex items-center justify-between bg-white border border-neutral-200 rounded-xl px-6 py-4">
          <div className="text-xs text-neutral-500">
            {savedAt
              ? `Last saved ${new Date(savedAt).toLocaleString()}`
              : 'No saved config yet — defaults to Sarah / Expressive on iOS.'}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg bg-amber-400 text-neutral-900 text-sm font-bold hover:bg-amber-300 transition disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save config'}
          </button>
        </section>
      </main>
    </div>
  );
};

// ── Helpers

const Slider: React.FC<{
  label: string;
  description?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}> = ({ label, description, value, min, max, step, onChange }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <div className="text-sm font-medium text-neutral-700">{label}</div>
      <div className="text-xs text-neutral-500 font-mono">{value.toFixed(2)}</div>
    </div>
    {description && <div className="text-xs text-neutral-400 mb-2">{description}</div>}
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-neutral-900"
    />
  </div>
);

type AdminTabId =
  | 'users'
  | 'wordbank'
  | 'voice'
  | 'sfx'
  | 'design'
  | 'emailSequences'
  | 'emailLogs'
  | 'notificationSequences'
  | 'notificationLogs'
  | 'purchaseLogs';

const AdminTabs: React.FC<{ current: AdminTabId }> = ({ current }) => {
  const tabs: Array<{ id: AdminTabId; to: string; label: string }> = [
    { id: 'users', to: '/admin/users', label: 'Users' },
    { id: 'wordbank', to: '/admin/wordbank', label: 'Wordbank' },
    { id: 'voice', to: '/admin/voice', label: 'Voice' },
    { id: 'sfx', to: '/admin/sfx', label: 'Sound library' },
    { id: 'design', to: '/admin/design-system', label: 'Design system' },
    { id: 'emailSequences', to: '/admin/email-sequences', label: 'Email sequences' },
    { id: 'emailLogs', to: '/admin/email-logs', label: 'Email logs' },
    { id: 'notificationSequences', to: '/admin/notification-sequences', label: 'Notification sequences' },
    { id: 'notificationLogs', to: '/admin/notification-logs', label: 'Notification logs' },
    { id: 'purchaseLogs', to: '/admin/purchase-logs', label: 'Purchase logs' },
  ];
  return (
    <nav className="flex gap-2 flex-wrap">
      {tabs.map((tab) => (
        <Link
          key={tab.id}
          to={tab.to}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
            current === tab.id
              ? 'bg-neutral-900 text-white border-neutral-900'
              : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
          }`}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
};

export { AdminTabs };
